import React, { useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { AddUserEmployerSideSchema } from "../../../../../../schemas/AddUserEmployerSideSchema";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { createInvitation } from "../../../../../../data/invitation";
import { Invitation } from "../../../../../../dataTypes/Invitation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faEnvelope,
  faBriefcase,
  faPaperPlane,
} from "@fortawesome/free-solid-svg-icons";
import { cn } from "../../../../../../util/cn";
import { TOnSendInvite } from "../../Pages/Users_NewUI";

type TAddUserModal = {
  onDone: ({ successText, errorText }: TOnSendInvite) => void;
  employer: any;
  user: any;
};

type FormValues = z.infer<typeof AddUserEmployerSideSchema>;

const fieldShell =
  "flex items-center w-full h-[54px] rounded-[12px] border border-[#D5E0E6] bg-white px-4 gap-3 transition-all duration-200 focus-within:border-[#15415E] focus-within:ring-2 focus-within:ring-[#15415E]/15";

const fieldInputClassName =
  "flex-1 h-full bg-transparent border-0 outline-none shadow-none rounded-none ring-0 focus:ring-0 focus:border-0 focus:outline-none text-[15px] text-[#15415E] placeholder:text-[#A5BDC5] !shadow-none";

const AddUserModal = ({ onDone, employer, user }: TAddUserModal) => {
  const domain = useMemo(() => {
    const domainUrl = employer?.domain || "";
    return domainUrl.replace(/^(https?:\/\/)/, "");
  }, [employer?.domain]);

  function getRootDomain(url: string): string {
    try {
      if (!/^https?:\/\//i.test(url)) {
        url = "https://" + url;
      }

      const hostname = new URL(url).hostname;
      const parts = hostname.split(".");

      if (parts.length >= 2) {
        const tld = parts.pop();
        const root = parts.pop();
        return `${root}.${tld}`;
      }

      return hostname;
    } catch (e) {
      console.error("Invalid URL:", e);
      return "";
    }
  }

  const domainFromUrl = getRootDomain(employer?.domain);

  const form = useForm<FormValues>({
    resolver: zodResolver(AddUserEmployerSideSchema),
    defaultValues: {
      fullName: "",
      email: "",
      jobTitle: "",
    },
    reValidateMode: "onBlur",
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = form;

  const onSubmit = async ({ ...values }: FormValues) => {
    const invitationData: Invitation = {
      company: employer.displayName,
      employerId: employer.id,
      employerRole: "GEN",
      email: `${values.email}@${domainFromUrl}`,
      name: values.fullName,
      invitedBy: user.id,
      status: "pending",
      title: values.jobTitle,
      domain: domain,
      type: "E",
    };

    try {
      await new Promise((resolve) => {
        createInvitation(invitationData)
          .then(() => {
            onDone({
              successText: `Sent invitation to ${values.email}`,
            });
            resolve(true);
          })
          .catch((error) => {
            onDone({
              errorText: error?.message,
            });
            resolve(true);
          });
      });
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="px-1 sm:px-2 pb-2 pt-1">
      <div className="flex flex-col gap-6 sm:gap-7">
        <Controller
          control={control}
          name="fullName"
          render={(field) => (
            <div>
              <label
                htmlFor="fullName"
                className="block mb-2 text-[14px] font-[Gilroy-SemiBold] text-[#15415E]"
              >
                Full Name <span className="text-[#ED3535]">*</span>
              </label>
              <div
                className={cn(fieldShell, {
                  "border-[#ED3535] focus-within:border-[#ED3535] focus-within:ring-[#ED3535]/15":
                    !!errors?.fullName,
                })}
              >
                <FontAwesomeIcon
                  icon={faUser}
                  className="text-[#7A9099] w-4 h-4 shrink-0"
                />
                <input
                  {...field}
                  id="fullName"
                  type="text"
                  placeholder="Enter full name"
                  style={{ boxShadow: "none" }}
                  className={fieldInputClassName}
                />
              </div>
              {errors?.fullName?.message && (
                <p className="mt-1.5 text-sm text-left text-red-600">
                  {errors.fullName.message}
                </p>
              )}
            </div>
          )}
        />

        <Controller
          control={control}
          name="email"
          render={(field) => (
            <div>
              <label
                htmlFor="email"
                className="block mb-2 text-[14px] font-[Gilroy-SemiBold] text-[#15415E]"
              >
                Email <span className="text-[#ED3535]">*</span>
              </label>
              <div
                className={cn(fieldShell, {
                  "border-[#ED3535] focus-within:border-[#ED3535] focus-within:ring-[#ED3535]/15":
                    !!errors?.email,
                })}
              >
                <FontAwesomeIcon
                  icon={faEnvelope}
                  className="text-[#7A9099] w-4 h-4 shrink-0"
                />
                <input
                  {...field}
                  id="email"
                  type="text"
                  placeholder="username"
                  style={{ boxShadow: "none" }}
                  className={cn(fieldInputClassName, "min-w-0")}
                />
                {domainFromUrl && (
                  <span className="shrink-0 pl-2 border-l border-[#E8EEF2] text-[14px] text-[#7A9099] font-[Gilroy-Medium] whitespace-nowrap">
                    @{domainFromUrl}
                  </span>
                )}
              </div>
              {errors?.email?.message && (
                <p className="mt-1.5 text-sm text-left text-red-600">
                  {errors.email.message}
                </p>
              )}
            </div>
          )}
        />

        <Controller
          control={control}
          name="jobTitle"
          render={(field) => (
            <div>
              <label
                htmlFor="jobTitle"
                className="block mb-2 text-[14px] font-[Gilroy-SemiBold] text-[#15415E]"
              >
                Job Title <span className="text-[#ED3535]">*</span>
              </label>
              <div
                className={cn(fieldShell, {
                  "border-[#ED3535] focus-within:border-[#ED3535] focus-within:ring-[#ED3535]/15":
                    !!errors?.jobTitle,
                })}
              >
                <FontAwesomeIcon
                  icon={faBriefcase}
                  className="text-[#7A9099] w-4 h-4 shrink-0"
                />
                <input
                  {...field}
                  id="jobTitle"
                  type="text"
                  placeholder="Enter job title"
                  style={{ boxShadow: "none" }}
                  className={fieldInputClassName}
                />
              </div>
              {errors?.jobTitle?.message && (
                <p className="mt-1.5 text-sm text-left text-red-600">
                  {errors.jobTitle.message}
                </p>
              )}
            </div>
          )}
        />
      </div>

      <button
        className={cn(
          "mt-8 sm:mt-9 flex justify-center items-center gap-2.5 w-full h-[54px] rounded-[14px] bg-[#15415E] text-white transition-all duration-200",
          "hover:bg-[#1a5278] active:bg-[#123348] active:scale-[0.99]",
          "disabled:bg-[#636b71] disabled:cursor-not-allowed disabled:active:scale-100",
        )}
        type="submit"
        disabled={isSubmitting}
      >
        {!isSubmitting && (
          <FontAwesomeIcon icon={faPaperPlane} className="w-4 h-4" />
        )}
        <span className="font-[Gilroy-Bold] font-[400] text-[16px] leading-[20px]">
          {isSubmitting ? "Sending Invitation..." : "Send Invitation"}
        </span>
      </button>
    </form>
  );
};

export default AddUserModal;
